import { OrderTabsPage } from './order-tabs/OrderTabsPage'
import { FiskalClosuresPage } from './fiskal-closures/FiskalClosuresPage'
import { FiskalReceiptsPage } from './fiskal-receipts/FiskalReceiptsPage'
import { FiskalSettingsPage } from './fiskal-settings/FiskalSettingsPage'
import { KasaLogsPage } from './kasa-logs/KasaLogsPage'
import { SummariesPage } from './summaries/SummariesPage'
import { RevenueHandoversPage } from './revenue-handovers/RevenueHandoversPage'

/**
 * `manager-kasa` — the fiscal register: settings, receipts, closures, logs,
 * handovers and summaries.
 *
 * Ported: kasa-logs, fiskal-receipts, revenue-handovers, fiskal-settings,
 * fiskal-closures, summaries. The
 * virtual-receipt renderer this module was going to own lives in `@agos/shared`
 * instead: the restaurant till and the dispatch board
 * print one too, and §5 forbids a module importing another — the boundary lint
 * caught it before the first commit.
 *
 * All six ported.
 *
 * Nothing here invents a tax figure. §11.4: a virtual receipt has no UID because
 * it never reached the authority's device, and every screen that shows one has to
 * say so.
 */
export const managerKasaModule = {
  name: 'manager-kasa',
  routes: [
    {
      path: '/fiskal-receipts',
      // Legacy opens this to `fiskal` *or* `fiskal_receipts_view`, so somebody who
      // may read the receipts need not be able to configure the register.
      page: 'fiskal-receipts',
      guard: { anyPermission: ['fiskal', 'fiskal_receipts_view'] },
      component: FiskalReceiptsPage,
    },
    {
      // Not in the sidebar — legacy reaches it only by URL (FB-004).
      path: '/kasa-logs',
      page: 'kasa-logs',
      guard: { permission: 'fiskal' },
      component: KasaLogsPage,
    },
    {
      // Legacy opens this to `fiskal`, `fiskal_closures_view` *or* `dispatch` — a
      // dispatcher closes the delivery register at the end of a shift.
      path: '/fiskal-closures',
      page: 'fiskal-closures',
      guard: { anyPermission: ['fiskal', 'fiskal_closures_view', 'dispatch'] },
      component: FiskalClosuresPage,
    },
    {
      // §9.407 - **`fiskal_settings`, and the tier gate the nav entry already carried.** The owner
      // settled it: *"both on `fiskal_settings`."*
      //
      // This read `{ permission: 'fiskal' }` with a comment arguing that *everything here ends on a
      // tax document, so it is `fiskal` only*. The reasoning was sound and reached the opposite
      // conclusion: because everything here ends on a tax document, the **narrow** right is the
      // safer default - and `nav-tree.ts` had already chosen it, so the screen was hidden from the
      // waiter role and reachable by typing the URL. `franchiseModule` comes across too, because a
      // route without the tier gate its menu entry has is the drift `access.ts` was written about.
      path: '/fiskal',
      page: 'fiskal-settings',
      guard: { permission: 'fiskal_settings', franchiseModule: 'fiskal' },
      component: FiskalSettingsPage,
    },
    {
      path: '/summaries',
      page: 'summaries',
      guard: { permission: 'summaries' },
      component: SummariesPage,
    },
    {
      path: '/revenue-handovers',
      page: 'revenue-handovers',
      guard: { permission: 'revenue_handovers', franchiseModule: 'revenue_handovers' },
      component: RevenueHandoversPage,
    },
    {
      // §9.5's *na sekeru* screen — a **justified EXTRA**: legacy has no such page, and
      // `route-diff.test.ts` requires every extra to cite the upgrade it implements.
      //
      // The guard mirrors every endpoint on `OrderTabController`, which is `{main_admin, admin, waiter}`:
      // a waiter opens and settles tabs, because a waiter is who the customer hands the money to.
      path: '/order-tabs',
      page: 'order-tabs',
      guard: { systemRoles: ['main_admin', 'admin', 'waiter'] },
      component: OrderTabsPage,
    },
  ],
} as const

export {
  OrderTabsPage,
  FiskalClosuresPage,
  FiskalReceiptsPage,
  FiskalSettingsPage,
  KasaLogsPage,
  RevenueHandoversPage,
  SummariesPage,
}

